import { IStringLocalizer } from "../../../utilities/IStringLocalizer";


/**
 * SFTP Import Configuration Models
 * Contains all interfaces and types related to SFTP import configurations.
 */

export interface SftpRecentRunHistory {
    executionId: string;
    configId: string;
    importId: string;
    executionMode: SftpExecutionMode;
    scheduledTime: string;
    executionDate: string;
    executionStatus: SftpImportQueueStatus;
    errorMessage: string;
    filePath: string;
    triggeredBy?: string;
}


export enum SftpExecutionMode
{
    Scheduled = "Scheduled",
    Manual = "Manual"
}

export enum SftpImportQueueStatus
{
    Queued = "Queued",
    QueuingFailed = "QueuingFailed",
    Queuing = "Queuing"
}

export enum FrequencyEnum{
    Daily = "Daily",
    Weekly = "Weekly",
    BiWeekly = "BiWeekly"
}

export enum ImportType { Products, Materials, Suppliers }
export enum InventoryMode { One, Multiple }
export enum DataType { Bom, Summary, ExternalDbIdUpdate, Scip }
export enum WorkflowStep { 
    Connection = "Connection",
    FileHandling = "FileHandling", 
    DefaultHandling = "DefaultHandling",
    ConfigureLevels = "ConfigureLevels",
    ColumnMapping = "ColumnMapping",
    Schedule = "Schedule",
    Review = "Review"
}

/**
 * Backend ImportTypeEnum values for mapping
 */
export enum ImportTypeEnum {
    EmptyMaterial = 0,
    EmptyProduct = 1,
    SingleMaterialSpreadsheet = 2,
    SingleProductSpreadsheet = 3,
    SingleMaterialSds = 4,
    SingleProductSds = 5,
    MaterialLibrary = 6,
    ProductLibrary = 7,
    SdsLibrary = 8,
    BulkSurveyResponseBomSpreadsheet = 9,
    BulkScipDataImport = 10,
    MyGreenLabProductsImport = 11,
    MyGreenLabProductsBulkGenerateLabels = 12,
    BulkSummaryDataImport = 13,
    BulkSupplierDataImport = 19,
    BulkExternalDbIdUpdate = 21
}

/**
 * Represents a column mapping between a header name and column ID
 * Matches the InventoryColumnMapping record structure from the backend
 */
export interface InventoryColumnMapping {
    headerName: string;
    columnId: string;  // This is the columnId in the backend
    level?: number | null;
}

/**
 * Maps frontend ImportType, InventoryMode, and DataType to ImportTypeEnum
 */
export function mapToImportTypeEnum(importType?: number, inventoryMode?: number, dataType?: number): ImportTypeEnum {
    const fImportType = importType !== undefined ? importType as ImportType : null;
    const fInventoryMode = inventoryMode !== undefined ? inventoryMode as InventoryMode : null;
    const fDataType = dataType !== undefined ? dataType as DataType : null;

    // Suppliers -> BulkSupplierDataImport (InventoryMode and DataType not relevant)
    if (fImportType === ImportType.Suppliers) {
        return ImportTypeEnum.BulkSupplierDataImport;
    }

    // Summary -> BulkSummaryDataImport (both One and Multiple map to same)
    if (fDataType === DataType.Summary) {
        return ImportTypeEnum.BulkSummaryDataImport;
    }

    if (fDataType === DataType.ExternalDbIdUpdate) {
        return ImportTypeEnum.BulkExternalDbIdUpdate;
    }

    // Scip -> BulkScipDataImport (both One and Multiple map to same)
    if (fDataType === DataType.Scip) {
        return ImportTypeEnum.BulkScipDataImport;
    }

    // Mat/Prod Single Bom -> Single prod/mat spreadsheet
    if (fInventoryMode === InventoryMode.One && fDataType === DataType.Bom) {
        return fImportType === ImportType.Materials 
            ? ImportTypeEnum.SingleMaterialSpreadsheet
            : ImportTypeEnum.SingleProductSpreadsheet;
    }

    // Prod/Mat Multiple Bom -> Prod/Mat Library
    if (fInventoryMode === InventoryMode.Multiple && fDataType === DataType.Bom) {
        return fImportType === ImportType.Materials
            ? ImportTypeEnum.MaterialLibrary
            : ImportTypeEnum.ProductLibrary;
    }

    // Default fallback
    return ImportTypeEnum.SingleMaterialSpreadsheet;
}

/**
 * Maps ImportTypeEnum back to frontend ImportType, InventoryMode, and DataType
 */
export function mapFromImportTypeEnum(importTypeEnum: ImportTypeEnum | string): { importType?: number; inventoryMode?: number; dataType?: number } {
    // Convert string enum name to numeric value if needed
    let enumValue: ImportTypeEnum;
    if (typeof importTypeEnum === 'string') {
        // Convert string name to enum value
        enumValue = ImportTypeEnum[importTypeEnum as keyof typeof ImportTypeEnum];
    } else {
        enumValue = importTypeEnum;
    }
    
    switch (enumValue) {
        case ImportTypeEnum.SingleMaterialSpreadsheet:
            return { importType: ImportType.Materials, inventoryMode: InventoryMode.One, dataType: DataType.Bom };
        case ImportTypeEnum.SingleProductSpreadsheet:
            return { importType: ImportType.Products, inventoryMode: InventoryMode.One, dataType: DataType.Bom };
        case ImportTypeEnum.MaterialLibrary:
            return { importType: ImportType.Materials, inventoryMode: InventoryMode.Multiple, dataType: DataType.Bom };
        case ImportTypeEnum.ProductLibrary:
            return { importType: ImportType.Products, inventoryMode: InventoryMode.Multiple, dataType: DataType.Bom };
        case ImportTypeEnum.BulkSummaryDataImport:
            return { dataType: DataType.Summary };
        case ImportTypeEnum.BulkExternalDbIdUpdate:
            return { dataType: DataType.ExternalDbIdUpdate };
        case ImportTypeEnum.BulkScipDataImport:
            return { dataType: DataType.Scip };
        case ImportTypeEnum.BulkSupplierDataImport:
            return { importType: ImportType.Suppliers };
        default:
            return { importType: ImportType.Materials, inventoryMode: InventoryMode.One, dataType: DataType.Bom };
    }
}

export interface SftpConfigurationGetByOwnerResponse {
    configId: string;
    frequency: string;
    createdBy: string;
    isActive: boolean;
}

export interface SftpImportConfigurationSetStatusRequest {
    id: string;
    isActive: boolean;
}

export interface SftpImportConfiguration {
    configId?: string;
    configurationName: string;
    isActive: boolean;
    frequency: FrequencyEnum;
    startTime: string;
    timeZone?: string;
    importType?: ImportTypeEnum;  // Simplified - TypeScript handles string enum names automatically
    name?: string;
    description?: string;
    plant?: string;
    supplierName?: string;
    productNumber?: string;
    supplierPartNumber?: string;
    externalDbId?: string;
    fileName: string;
    subPath: string;
    spreadsheetHeadersToColumnIdMappings: InventoryColumnMapping[];
    postImportAction: PostImportActionEnum;
    hasHeader: boolean;
    headerEndRow?: number;
    hasFooter: boolean;
    footerStartRow?: number;
    sheetName: string;
    tags: string[]; 
    calculationMode: CalculationModeType;
    
    // Individual legacy settings fields for frontend compatibility
    shouldExtractAndLink: boolean;
    shouldExtractLeafiestMaterials: boolean;
    extractAndLinkShouldOverwriteSubmaterials: boolean;
    shouldMatchMaterials: boolean;
    inventoryIdentificationMatchMode?: InventoryIdentificationMatchMode;
    supplierIdentificationMatchMode?: SupplierIdentificationMatchMode;
    
    libraryImportExistingInventoryMode?: LibraryImportExistingInventoryModeType;
    libraryImportShouldUpdateSummaryRows: boolean;
    libraryImportNonSummaryRowsMode?: LibraryImportNonSummaryRowsModeType;
    importHierarchyOptionType?: ImportHierarchyOption;
}

// Extended interface for use in workflow UI - includes frontend-specific enum values
export interface SftpImportConfigurationWorkflow extends Omit<SftpImportConfiguration, 'importType'> {
    importType?: ImportType;  // Override base importType to use UI enum for workflow
    dataType?: DataType;
    inventoryMode?: InventoryMode;
}

// =============================================================================
// New enums mirrored from backend for data integrity
// =============================================================================

// CalculationModeType from DbInventory.CalculationModeType
export enum CalculationModeType {
    None = "None",
    ToxnotStandardV2 = "ToxnotStandardV2",
    Relative = "Relative",
    Absolute_TotalWeightByLeafNodes = "Absolute_TotalWeightByLeafNodes",
    Absolute_TotalWeightByAbsoluteEntries = "Absolute_TotalWeightByAbsoluteEntries",
    QuantityUnitWeight_TotalWeightByLeafNodes = "QuantityUnitWeight_TotalWeightByLeafNodes",
    QuantityUnitWeight_TotalWeightByAbsoluteEntries = "QuantityUnitWeight_TotalWeightByAbsoluteEntries",
    Material_Relative = "Material_Relative",
    Material_Weight_TotalWeightByLeafNodes = "Material_Weight_TotalWeightByLeafNodes",
    Material_QuantityUnitWeight_TotalWeightByLeafNodes = "Material_QuantityUnitWeight_TotalWeightByLeafNodes",
    CalculateUnitWeightsFromBottomUp = "CalculateUnitWeightsFromBottomUp"
}

// InventoryIdentificationMatchMode from DbInventory.InventoryIdentificationMatchMode
export enum InventoryIdentificationMatchMode {
    Default = "Default",
    OnlyName = "OnlyName",
    IgnoreSupplier = "IgnoreSupplier",
    IgnoreDescription = "IgnoreDescription",
    Strict = "Strict",
    IgnoreSupplierAndDescription = "IgnoreSupplierAndDescription",
    ExchangeV2 = "ExchangeV2",
    IgnoreSupplierDescriptionAndExternalDbId = "IgnoreSupplierDescriptionAndExternalDbId"
}

// SupplierIdentificationMatchMode from SupplierIdentificationLookup
export enum SupplierIdentificationMatchMode {
    Default = "Default",
    UserFacingSupplierIdOnly = "UserFacingSupplierIdOnly",
    NameOnly = "NameOnly",
    NameAndUserFacingSupplierId = "NameAndUserFacingSupplierId"
}

export enum PostImportActionEnum {
    Delete = "Delete",
    Archive = "Archive",
    Rename = "Rename",
    Skip = "Skip"
}

// LibraryImportExistingInventoryModeType from DbImportContext.LibraryImportExistingInventoryModeType
export enum LibraryImportExistingInventoryModeType {
    SkipExisting = "SkipExisting",
    OverwriteExisting = "OverwriteExisting",
    WriteNewWithUniquifiedName = "WriteNewWithUniquifiedName"
}

// LibraryImportNonSummaryRowsModeType (simplified to the two values actually used)
export enum LibraryImportNonSummaryRowsModeType {
    LeaveNonSummaryRowsIntact = "LeaveNonSummaryRowsIntact",
    ReplaceNonSummaryRows = "ReplaceNonSummaryRows"
}

// ImportHierarchyOption from DbImportContext.ImportHierarchyOption (string)
export enum ImportHierarchyOption {
    LevelColumn = "LevelColumn",
    RepeatedMaterialIdentifiers = "RepeatedMaterialIdentifiers",
    LevelColumnWithMultipleLevelsPerRow = "LevelColumnWithMultipleLevelsPerRow"
}

// =============================================================================
// Enum Description Mappings for UI Display - Functions that accept localizer
// =============================================================================


export function getSftpImportQueueStatusDescriptions(localizer: IStringLocalizer): Record<string, string> {
    return {
        [SftpImportQueueStatus.Queued]: localizer.localizeString('Success'),
        [SftpImportQueueStatus.Queuing]: localizer.localizeString('In Progress'),
        [SftpImportQueueStatus.QueuingFailed]: localizer.localizeString('Failed')
    };
}


export function getSftpExecutionModeDescriptions(localizer: IStringLocalizer): Record<string, string> {
    return {
        [SftpExecutionMode.Manual]: localizer.localizeString('Manual'),
        [SftpExecutionMode.Scheduled]: localizer.localizeString('Scheduled')
    };
}

export function getSftpImportFrequencyDescriptions(localizer: IStringLocalizer): Record<string, string> {
    return {
        [FrequencyEnum.Daily]: localizer.localizeString('Daily'),
        [FrequencyEnum.Weekly]: localizer.localizeString('Weekly'),
        [FrequencyEnum.BiWeekly]: localizer.localizeString('Bi-Weekly')
    };
}

export function getPostImportActionDescriptions(localizer: IStringLocalizer): Record<string, string> {
    return {
        [PostImportActionEnum.Delete]: localizer.localizeString('Delete after import'),
        [PostImportActionEnum.Archive]: localizer.localizeString('Move to archive directory'),
        [PostImportActionEnum.Rename]: localizer.localizeString('Rename with timestamp suffix'),
        [PostImportActionEnum.Skip]: localizer.localizeString('Skip file in place')
    };
}

export function getInventoryIdentificationMatchModeDescriptions(localizer: IStringLocalizer): Record<string, string> {
    return {
        [InventoryIdentificationMatchMode.Default]: localizer.localizeString('Use Name and Description, Plant, or Supplier when provided (Default)'),
        [InventoryIdentificationMatchMode.Strict]: localizer.localizeString('Use all of Name, Description, Plant, and Supplier and require strict matches on empty values'),
        [InventoryIdentificationMatchMode.OnlyName]: localizer.localizeString('Only use Name, ignore Description, Plant, and Supplier'),
        [InventoryIdentificationMatchMode.IgnoreSupplier]: localizer.localizeString('Only use Name and Description, Plant when provided, but ignore Supplier (use when updating Supplier names)'),
        [InventoryIdentificationMatchMode.IgnoreDescription]: localizer.localizeString('Ignore Description and only use Name, Plant, and Supplier')
    };
}

export function getSupplierIdentificationMatchModeDescriptions(localizer: IStringLocalizer): Record<string, string> {
    return {
        [SupplierIdentificationMatchMode.Default]: localizer.localizeString('Use Name and Supplier ID when provided (Default)'),
        [SupplierIdentificationMatchMode.NameAndUserFacingSupplierId]: localizer.localizeString('Supplier ID and Supplier Name'),
        [SupplierIdentificationMatchMode.UserFacingSupplierIdOnly]: localizer.localizeString('Supplier ID'),
        [SupplierIdentificationMatchMode.NameOnly]: localizer.localizeString('Supplier Name')
    };
}

export function getCalculationModeTypeDescriptions(localizer: IStringLocalizer): Record<string, string> {
    return {
        [CalculationModeType.None]: localizer.localizeString('No Auto-calculations'),
        [CalculationModeType.ToxnotStandardV2]: localizer.localizeString('3E Exchange Standard Autocalculations (Recommended)'),
        [CalculationModeType.Relative]: localizer.localizeString('Percentages (Relative)'),
        [CalculationModeType.Absolute_TotalWeightByLeafNodes]: localizer.localizeString('Weights (Absolute) -- Use substance rows for summing Total Weight'),
        [CalculationModeType.Absolute_TotalWeightByAbsoluteEntries]: localizer.localizeString('Weights (Absolute) -- Use absolute weights entered for summing Total Weight'),
        [CalculationModeType.QuantityUnitWeight_TotalWeightByLeafNodes]: localizer.localizeString('Quantities/Unit Weights -- Use substance rows for summing Total Weight'),
        [CalculationModeType.QuantityUnitWeight_TotalWeightByAbsoluteEntries]: localizer.localizeString('Quantities/Unit Weights -- Use absolute quantity/unit weights entered for summing Total Weight'),
        [CalculationModeType.Material_Relative]: localizer.localizeString('Material Relative -- Calculate Material % by Weight from Product % by Weight'),
        [CalculationModeType.Material_Weight_TotalWeightByLeafNodes]: localizer.localizeString('Material Weights -- Calculate Material % by Weight from Weight'),
        [CalculationModeType.Material_QuantityUnitWeight_TotalWeightByLeafNodes]: localizer.localizeString('Material Quantities/Unit Weights -- Calculate Material % by Weight from Quanity and Unit Weight'),
        [CalculationModeType.CalculateUnitWeightsFromBottomUp]: localizer.localizeString('Calculate Unit Weights from the lowest level materials upwards')
    };
}

export function getLibraryImportExistingInventoryModeTypeDescriptions(localizer: IStringLocalizer): Record<string, string> {
    return {
        [LibraryImportExistingInventoryModeType.OverwriteExisting]: localizer.localizeString('Overwrite Existing Products/Materials with the Import'),
        [LibraryImportExistingInventoryModeType.WriteNewWithUniquifiedName]: localizer.localizeString('Import Conflicts as New Products/Materials with Updated Names'),
        [LibraryImportExistingInventoryModeType.SkipExisting]: localizer.localizeString('Skip any Import Conflicts')
    };
}

export function getImportHierarchyOptionDescriptions(localizer: IStringLocalizer): Record<string, string> {
    return {
        [ImportHierarchyOption.LevelColumn]: localizer.localizeString('No, there is no sub-materials hierarchy.'),
        [ImportHierarchyOption.LevelColumnWithMultipleLevelsPerRow]: localizer.localizeString('Yes, hierarchy is indicated by a Level column.'),
        [ImportHierarchyOption.RepeatedMaterialIdentifiers]: localizer.localizeString('Yes, hierarchy is indicated by repeated product/material names, or multiple levels per row.')
    };
}

export function getImportTypeEnumToText(localizer: IStringLocalizer): Record<string, string> {
    return {
        [ImportTypeEnum.SingleMaterialSpreadsheet]: localizer.localizeString('Single Material'),
        [ImportTypeEnum.SingleProductSpreadsheet]: localizer.localizeString('Single Product'),
        [ImportTypeEnum.MaterialLibrary]: localizer.localizeString('Material Library'),
        [ImportTypeEnum.ProductLibrary]: localizer.localizeString('Product Library'),
        [ImportTypeEnum.BulkScipDataImport]: localizer.localizeString('SCIP Data Import'),
        [ImportTypeEnum.BulkSummaryDataImport]: localizer.localizeString('Summary Data Import'),
        [ImportTypeEnum.BulkExternalDbIdUpdate]: localizer.localizeString('Bulk Update External DB ID'),
        [ImportTypeEnum.BulkSupplierDataImport]: localizer.localizeString('Supplier Data Import'),
    };
}

export function getStepNames(localizer: IStringLocalizer): Record<string, string> {
        return {
            [WorkflowStep.Connection]: localizer.localizeString("Connection & Authentication"),
            [WorkflowStep.FileHandling]: localizer.localizeString("File Handling"),
            [WorkflowStep.DefaultHandling]: localizer.localizeString("Default Handling"),
            [WorkflowStep.ConfigureLevels]: localizer.localizeString("Configure Levels"),
            [WorkflowStep.ColumnMapping]: localizer.localizeString("Column Mapping"),
            [WorkflowStep.Schedule]: localizer.localizeString("Schedule & Post-Import"),
            [WorkflowStep.Review]: localizer.localizeString("Review")
        };
    };

/**
 * Helper function to get enum key by value for display purposes
 */
export function getEnumKeyByValue(enumObj: any, value: any): string {
    if (value === null || value === undefined) return "Not Set";
    return Object.keys(enumObj).find(key => enumObj[key] === value) || "Unknown";
}

// =============================================================================
// Conversion functions for UI workflow integration
// =============================================================================

/**
 * Converts workflow configuration to base configuration for backend communication
 */
export function convertWorkflowToBackend(workflowConfig: SftpImportConfigurationWorkflow): SftpImportConfiguration {
    const { ...baseConfig } = workflowConfig as any;

    const importTypeEnum = mapToImportTypeEnum(
        workflowConfig.importType,
        workflowConfig.inventoryMode,
        workflowConfig.dataType
    );
    return {
        ...baseConfig,
        headerEndRow: workflowConfig.hasHeader && workflowConfig.headerEndRow !== undefined ? workflowConfig.headerEndRow -1 : workflowConfig.headerEndRow,
        footerStartRow: workflowConfig.hasFooter && workflowConfig.footerStartRow !== undefined ? workflowConfig.footerStartRow -1 : workflowConfig.footerStartRow,
        importType: importTypeEnum
    } as SftpImportConfiguration;
}

/**
 * Converts base configuration to workflow configuration for UI use
 */
export function convertBackendToWorkflow(baseConfig: SftpImportConfiguration): SftpImportConfigurationWorkflow {
    
    // Convert backend ImportTypeEnum to frontend enum values for UI components
    const mappedValues = baseConfig.importType !== undefined && baseConfig.importType !== null
        ? mapFromImportTypeEnum(baseConfig.importType)
        : { importType: undefined, inventoryMode: undefined, dataType: undefined };

    // Clean up tags array
    const tags = Array.isArray(baseConfig.tags)
        ? (baseConfig.tags as string[]).map(t => t.trim()).filter(t => t.length > 0)
        : [];

    return {
        ...baseConfig,
        timeZone: 'EST',
        importType: mappedValues.importType,
        inventoryMode: mappedValues.inventoryMode,
        dataType: mappedValues.dataType,
        tags,
        headerEndRow: baseConfig.hasHeader && baseConfig.headerEndRow !== undefined ? baseConfig.headerEndRow + 1 : baseConfig.headerEndRow,
        footerStartRow: baseConfig.hasFooter && baseConfig.footerStartRow !== undefined ? baseConfig.footerStartRow + 1 : baseConfig.footerStartRow
    } as SftpImportConfigurationWorkflow;
}

export function getSupplierColumnMappings(localizer: IStringLocalizer) {
    const basicFields = [
        { columnId: "supplier-name", description: localizer.localizeString("Supplier Name") },
        { columnId: "supplier-id", description: localizer.localizeString("Supplier ID") },
        { columnId: "address", description: localizer.localizeString("Address") },
        { columnId: "notes", description: localizer.localizeString("Notes") },
        { columnId: "tags", description: localizer.localizeString("Tags") },
        { columnId: "status", description: localizer.localizeString("Status") }
    ];

    const contactFields = [];
    for (let i = 1; i <= 10; i++) {
        contactFields.push(
            { columnId: `contact-name-${i}`, description: localizer.localizeString(`Contact Name ${i}`) },
            { columnId: `contact-email-${i}`, description: localizer.localizeString(`Contact Email ${i}`) },
            { columnId: `contact-label-${i}`, description: localizer.localizeString(`Contact Label ${i}`) },
            { columnId: `contact-phone-${i}`, description: localizer.localizeString(`Contact Phone ${i}`) },
            { columnId: `contact-country-${i}`, description: localizer.localizeString(`Contact Country ${i}`) }
        );
    }

    return [...basicFields, ...contactFields];
}
